import React, { useState, useEffect } from 'react';
import { FiEdit3, FiClock, FiFileText } from 'react-icons/fi';

const EssayQuestion = ({ 
  question, 
  onAnswer, 
  currentAnswer = null,
  isSubmitted = false,
  timeSpent = 0 
}) => {
  const [essayText, setEssayText] = useState(currentAnswer?.text || '');
  const [wordCount, setWordCount] = useState(0);
  const [charCount, setCharCount] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [blankAnswers, setBlankAnswers] = useState({});

  // Function to render question text with input fields for parentheses
  const renderQuestionWithInputs = (text) => {
    if (!text) return '';
    
    // Split by parentheses to find blanks - look for ( ) pattern
    const parts = text.split(/\(\s*\)/g);
    const blanks = [];
    
    if (parts.length > 1) {
      for (let i = 0; i < parts.length - 1; i++) {
        blanks.push({
          id: i,
          beforeText: parts[i],
          afterText: parts[i + 1]
        });
      }
    }
    
    if (blanks.length === 0) {
      return <span>{text}</span>;
    }
    
    return (
      <>
        {blanks.map((blank, index) => (
          <span key={blank.id}>
            {blank.beforeText}
            <input
              type="text"
              value={blankAnswers[index] || ''}
              onChange={(e) => {
                const newAnswers = { ...blankAnswers, [index]: e.target.value };
                setBlankAnswers(newAnswers);
              }}
              placeholder={`Blank ${index + 1}`}
              className="mx-2 px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500 text-sm text-center bg-white transition-all duration-200 w-24"
              disabled={isSubmitted}
            />
            {index === blanks.length - 1 && blank.afterText}
          </span>
        ))}
      </>
    );
  };

  // Calculate word and character count
  useEffect(() => {
    const text = essayText.trim();
    const words = text ? text.split(/\s+/).length : 0;
    const chars = text.length;
    
    setWordCount(words);
    setCharCount(chars);
  }, [essayText]);

  // Initialize blank answers from current answer if it exists
  useEffect(() => {
    if (currentAnswer?.blankAnswers) {
      setBlankAnswers(currentAnswer.blankAnswers);
    }
  }, [currentAnswer]);

  const handleTextChange = (e) => {
    if (isSubmitted) return; // Prevent changes after submission
    setEssayText(e.target.value);
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    
    if (!essayText.trim()) {
      alert('Please write your essay answer before submitting.');
      return;
    }

    setIsSubmitting(true);
    
    try {
      await onAnswer({
        questionId: question.id,
        text: essayText.trim(),
        blankAnswers: Object.keys(blankAnswers).length > 0 ? blankAnswers : undefined,
        type: 'ESSAY',
        wordCount,
        charCount,
        timeSpent
      });
    } catch (error) {
      console.error('Error submitting essay answer:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getWordCountColor = () => {
    if (wordCount < 50) return 'text-gray-600';
    if (wordCount < 100) return 'text-orange-600';
    if (wordCount < 200) return 'text-yellow-600';
    return 'text-green-600';
  };

  return (
    <div className="essay-question bg-gray-100 p-4 sm:p-6 lg:p-8 mb-6">
      {/* Question Text */}
      <div className="mb-4 sm:mb-6">
        <div className="text-black text-sm sm:text-base font-bold leading-relaxed break-words">
          {renderQuestionWithInputs(question.text)}
        </div>
      </div>

      {/* Images if any */}
      {question.images && question.images.length > 0 && (
        <div className="mb-4 sm:mb-6">
          <h4 className="text-sm font-semibold text-black mb-3">Supporting Materials:</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {question.images.map((image, index) => (
              <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="relative group">
                  <img 
                    src={image.imageUrl} 
                    alt={image.altText || `Question image ${index + 1}`}
                    className="w-full h-auto max-h-48 sm:max-h-64 lg:max-h-80 object-contain cursor-pointer transition-transform duration-200 hover:scale-105"
                    onClick={() => window.open(image.imageUrl, '_blank')}
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all duration-200 flex items-center justify-center">
                    <span className="text-white opacity-0 group-hover:opacity-100 text-sm font-medium">Click to enlarge</span>
                  </div>
                </div>
                {image.altText && (
                  <div className="p-2 sm:p-3 bg-gray-50 text-xs sm:text-sm text-black break-words">
                    {image.altText}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Essay Input Area */}
      <div className="mb-4 sm:mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
          <label className="text-sm font-semibold text-black flex items-center gap-2">
            <FiEdit3 className="text-gray-500" />
            Your Essay Answer:
          </label>
          <div className="flex items-center gap-2 sm:gap-4 text-xs sm:text-sm">
            <span className={`font-medium ${getWordCountColor()} bg-gray-50 px-2 py-1 rounded`}>
              {wordCount} words
            </span>
            <span className="text-black bg-gray-50 px-2 py-1 rounded">
              {charCount} characters
            </span>
          </div>
        </div>
        
        <textarea
          value={essayText}
          onChange={handleTextChange}
          disabled={isSubmitted}
          placeholder="Write your detailed essay answer here. Be thorough and provide examples where appropriate..."
          className={`w-full h-48 sm:h-64 lg:h-80 p-3 sm:p-4 border-2 rounded-lg resize-y font-medium text-black leading-relaxed ${
            isSubmitted 
              ? 'bg-gray-50 border-gray-300 cursor-not-allowed' 
              : 'border-blue-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200'
          }`}
          style={{ fontFamily: 'inherit' }}
        />
        
        {/* Word count guidance */}
        <div className="mt-2 text-sm text-black">
          {wordCount < 50 && (
            <span className="text-gray-500">Your answer is quite short. Consider adding more detail.</span>
          )}
          {wordCount >= 50 && wordCount < 100 && (
            <span className="text-orange-600">📝 Good start! Consider expanding on your points.</span>
          )}
          {wordCount >= 100 && wordCount < 200 && (
            <span className="text-yellow-600">📖 Good length! Make sure to include examples and explanations.</span>
          )}
          {wordCount >= 200 && (
            <span className="text-green-600">✅ Excellent length! Your answer appears comprehensive.</span>
          )}
        </div>
      </div>

      {/* Expected Answer Template (if available) */}
      {question.options && question.options.length > 0 && question.options[0]?.text && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="text-sm font-semibold text-black mb-2">Answer Guidelines:</h4>
          <div className="text-sm text-black leading-relaxed">
            {question.options[0].text}
          </div>
        </div>
      )}



      {/* Submitted Answer Display */}
      {isSubmitted && (
        <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <h4 className="text-sm font-semibold text-black mb-2">Your Submitted Answer:</h4>
          <div className="text-sm text-black leading-relaxed whitespace-pre-wrap">
            {essayText}
          </div>
          <div className="mt-3 text-xs text-black">
            Submitted with {wordCount} words and {charCount} characters
          </div>
        </div>
      )}
    </div>
  );
};

export default EssayQuestion; 